import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';
import { Camera } from '@mediapipe/camera_utils';

const GREEN_HEX = '#00FF6A';
const CANVAS_WIDTH = 1280;
const CANVAS_HEIGHT = 720;

function createFileFromBlob(blob, extension = 'webm') {
  const filename = `greenscreen-${Date.now()}.${extension}`;
  return new File([blob], filename, { type: blob.type || `video/${extension}` });
}

export default function GreenScreenStudio({ onExport, onBack }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const bgVideoRef = useRef(null);
  const bgImageRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState('Initializing camera…');
  const [backgroundMode, setBackgroundMode] = useState('color');
  const [backgroundColor, setBackgroundColor] = useState(GREEN_HEX);
  const [bgMediaUrl, setBgMediaUrl] = useState(null);
  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  const segmentationRef = useRef(null);
  const cameraControllerRef = useRef(null);
  const backgroundModeRef = useRef('color');
  const backgroundColorRef = useRef(GREEN_HEX);

    useEffect(() => {
      backgroundModeRef.current = backgroundMode;
    }, [backgroundMode]);

    useEffect(() => {
      backgroundColorRef.current = backgroundColor;
    }, [backgroundColor]);

  const backgroundDescription = useMemo(() => {
    if (backgroundMode === 'color') return 'Solid chroma background';
    if (backgroundMode === 'image') return 'Still image backdrop';
    return 'Looping video backdrop';
  }, [backgroundMode]);

  const disposeMedia = useCallback(() => {
    if (cameraControllerRef.current) {
      cameraControllerRef.current.stop();
      cameraControllerRef.current = null;
    }
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    if (segmentationRef.current) {
      segmentationRef.current.close && segmentationRef.current.close();
      segmentationRef.current = null;
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: CANVAS_WIDTH, height: CANVAS_HEIGHT },
          audio: false,
        });
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        const segmentation = new SelfieSegmentation({
          locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
        });
        segmentation.setOptions({ modelSelection: 1 });
        segmentationRef.current = segmentation;

        const onResults = (results) => {
          const canvasEl = canvasRef.current;
          const videoEl = videoRef.current;
          if (!canvasEl || !videoEl) return;
          const ctx = canvasEl.getContext('2d');
          ctx.save();
          ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

          // Draw segmentation mask first
          ctx.drawImage(results.segmentationMask, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

          // Draw background where mask is not person
          ctx.globalCompositeOperation = 'source-out';
          const currentMode = backgroundModeRef.current;
          if (currentMode === 'color') {
            ctx.fillStyle = backgroundColorRef.current;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
          } else if (currentMode === 'image' && bgImageRef.current) {
            ctx.drawImage(bgImageRef.current, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
          } else if (currentMode === 'video' && bgVideoRef.current) {
            ctx.drawImage(bgVideoRef.current, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
          } else {
            ctx.fillStyle = backgroundColorRef.current;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
          }

          // Draw person on top
          ctx.globalCompositeOperation = 'destination-atop';
          ctx.drawImage(videoEl, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
          ctx.restore();
        };

        segmentation.onResults(onResults);

        const camera = new Camera(videoRef.current, {
          onFrame: async () => {
            if (!segmentationRef.current || !videoRef.current) return;
            await segmentationRef.current.send({ image: videoRef.current });
          },
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
        });
        cameraControllerRef.current = camera;
        await camera.start();
        setCameraReady(true);
        setStatus('Camera ready. Adjust your background below.');
      } catch (err) {
        console.error('Green screen init failed:', err);
        setError(err.message || 'Camera initialization failed');
        setStatus('Unable to access camera');
      }
    };

    init();
    return () => {
      disposeMedia();
    };
  }, [disposeMedia]);

  const handleBackgroundFile = useCallback((event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setBgMediaUrl(url);
    setBackgroundMode(type);
    backgroundModeRef.current = type;

    if (type === 'image' && bgImageRef.current) {
      bgImageRef.current.src = url;
    }
    if (type === 'video' && bgVideoRef.current) {
      bgVideoRef.current.src = url;
      bgVideoRef.current.loop = true;
      bgVideoRef.current.muted = true;
      bgVideoRef.current.play().catch(() => {});
    }
  }, []);

  const capturePhoto = useCallback(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    canvasEl.toBlob((blob) => {
      if (!blob) return;
      const file = createFileFromBlob(blob, 'png');
      const previewUrl = URL.createObjectURL(blob);
      onExport?.({ file, previewUrl, kind: 'photo' });
    }, 'image/png');
  }, [onExport]);

  const stopRecording = useCallback(() => {
    recorderRef.current?.stop();
  }, []);

  const startRecording = useCallback(() => {
    if (recording) {
      stopRecording();
      return;
    }
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const stream = canvasEl.captureStream(30);
    recordedChunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: 6_000_000,
    });
    recorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      setRecording(false);
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      const file = createFileFromBlob(blob, 'webm');
      const previewUrl = URL.createObjectURL(blob);
      onExport?.({ file, previewUrl, kind: 'video' });
    };

    mediaRecorder.start();
    setRecording(true);
    setStatus('Recording green screen video…');
  }, [onExport, recording, stopRecording]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <div className="p-4 flex items-center justify-between border-b border-white/10">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-white/40">Green Screen Studio</p>
          <h1 className="text-3xl font-bold">Virtual Background Capture</h1>
          <p className="text-white/60 text-sm mt-1">{backgroundDescription}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={capturePhoto}
            disabled={!cameraReady}
            className="px-4 py-2 rounded-full bg-white text-black font-semibold disabled:opacity-40"
          >
            Capture Photo
          </button>
          <button
            onClick={startRecording}
            disabled={!cameraReady}
            className={`px-4 py-2 rounded-full border text-sm font-semibold ${recording ? 'bg-red-500 border-red-500 text-white' : 'border-white/30 text-white/80'}`}
          >
            {recording ? 'Stop Recording' : 'Record Video'}
          </button>
          <button
            onClick={onBack}
            className="px-4 py-2 rounded-full border border-white/20 text-white/80 hover:bg-white/10"
          >
            Exit Studio
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/40 text-red-200 p-4 text-sm text-center">
          {error}
        </div>
      )}

      <div className="flex flex-col lg:flex-row flex-1">
        <div className="flex-1 relative bg-black">
          <video ref={videoRef} className="hidden" playsInline muted />
          <video ref={bgVideoRef} className="hidden" playsInline muted loop />
          <img ref={bgImageRef} className="hidden" alt="background" />
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            className="w-full h-full object-contain bg-black"
          />
          <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-black/60 text-sm">
            {status}
          </div>
        </div>

        <div className="w-full lg:w-[420px] border-t lg:border-t-0 lg:border-l border-white/10 bg-slate-950/70 p-6 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/40">Background type</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {['color', 'image', 'video'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => {
                    setBackgroundMode(mode);
                    backgroundModeRef.current = mode;
                  }}
                  className={`px-4 py-2 rounded-full border text-sm font-semibold ${backgroundMode === mode ? 'bg-white text-black' : 'border-white/20 text-white/70'}`}
                >
                  {mode === 'color' ? 'Solid green' : mode === 'image' ? 'Photo' : 'Video loop'}
                </button>
              ))}
            </div>
          </div>

          {backgroundMode === 'color' && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Pick a chroma color</p>
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => {
                  setBackgroundColor(e.target.value);
                  backgroundColorRef.current = e.target.value;
                }}
                className="w-16 h-16 rounded-full border border-white/20"
              />
            </div>
          )}

          {backgroundMode === 'image' && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Background still</p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleBackgroundFile(e, 'image')}
                className="block w-full text-sm text-white"
              />
              {bgMediaUrl && (
                <img src={bgMediaUrl} alt="Selected background" className="mt-3 rounded-xl border border-white/10" />
              )}
            </div>
          )}

          {backgroundMode === 'video' && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Looping video</p>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleBackgroundFile(e, 'video')}
                className="block w-full text-sm text-white"
              />
              {bgMediaUrl && (
                <video src={bgMediaUrl} className="mt-3 rounded-xl border border-white/10" autoPlay loop muted playsInline />
              )}
            </div>
          )}

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Tips</p>
            <ul className="text-sm text-white/70 space-y-1 list-disc list-inside">
              <li>Use a tripod or prop phone for best segmentation.</li>
              <li>Strong lighting increases edge fidelity.</li>
              <li>Capture in portrait by rotating your device and locking orientation.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
